import cn from 'classnames'
import { Nullable } from 'legacy/lut/classes/models/utils'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'

import { InputWrap } from 'core-components/library/inputs/InputWrapper'

import { IBaseNumberInputProps } from '../../models'

import { checkMinMax } from './helpers/checkMinMax'
import { getDecimalSign } from './helpers/getDecimalSign'
import { getFormattedValue } from './helpers/getFormattedValue'
import { getNormalizedValue } from './helpers/getNormalizedValue'

import styles from './BaseNumberInput.module.scss'

export const BaseNumberInput = forwardRef<HTMLInputElement, IBaseNumberInputProps>(
  (
    {
      align = 'left',
      autoComplete,
      classes,
      margin,
      label,
      value = '',
      wrapper,
      disabled,
      startContent,
      endContent,
      format = 'decimal',
      useGrouping = true,
      id,
      autoFocus,
      placeholder,
      min,
      max,
      onChange,
      onFocus,
      onBlur,
      onSelect,
      hasSpin = true,
      locales = 'en',
      maximumFractionDigits = 3,
    },
    forwardedRef
  ) => {
    const [focused, setFocused] = useState(false)
    const [displayValue, setDisplayValue] = useState('')
    const [decimalSign, setDecimalSign] = useState(getDecimalSign(locales))
    const internalRef = useRef<HTMLInputElement>(null)
    const [prevValue, setPrevValue] = useState(value)

    useImperativeHandle<Nullable<HTMLInputElement>, Nullable<HTMLInputElement>>(
      forwardedRef,
      () => internalRef.current
    )

    const handleOnChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const cursorPosition = e.target.selectionStart
        const inputValue = e.currentTarget.value
        const normalizedValue = getNormalizedValue({ value: inputValue, decimalSign })
        const validatedValue = checkMinMax({ value: normalizedValue, min, max })

        if (!validatedValue && normalizedValue) {
          return
        }

        if (onChange && (!isNaN(Number(validatedValue)) || validatedValue === '-')) {
          onChange(validatedValue)

          const formattedValue = getFormattedValue({
            value: validatedValue,
            format,
            useGrouping,
            locales,
            decimalSign,
            hasZero: true,
            maximumFractionDigits,
          })

          setDisplayValue(formattedValue)

          const prevCommas = Array.from(prevValue).filter((ch) => ch === ',').length
          const newCommas = Array.from(formattedValue).filter((ch) => ch === ',').length
          const commaDifference = newCommas - prevCommas

          // Reset cursor positon
          requestAnimationFrame(() => {
            if (internalRef.current && cursorPosition) {
              const newCursorPosition = cursorPosition + commaDifference
              internalRef.current.selectionStart = newCursorPosition
              internalRef.current.selectionEnd = newCursorPosition
            }
          })

          setPrevValue(formattedValue)
        }
      },
      [
        decimalSign,
        min,
        max,
        onChange,
        format,
        useGrouping,
        locales,
        maximumFractionDigits,
        prevValue,
      ]
    )

    const handleOnFocus = useCallback(() => {
      setFocused(true)
      if (onFocus) {
        onFocus()
      }
    }, [onFocus])

    const handleOnBlur = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setFocused(false)
        const normalizedValue = getNormalizedValue({ value: displayValue, decimalSign })

        const formattedValue = getFormattedValue({
          value: normalizedValue,
          format,
          useGrouping,
          locales,
          decimalSign,
          maximumFractionDigits,
        })

        if (formattedValue === '-') {
          setDisplayValue('0')
        } else {
          setDisplayValue(formattedValue)
        }

        if (onBlur) {
          onBlur(event)
        }
      },
      [decimalSign, displayValue, format, locales, onBlur, useGrouping, maximumFractionDigits]
    )

    useEffect(() => {
      setDecimalSign(getDecimalSign(locales))
    }, [locales])

    useEffect(() => {
      const formattedValue = getFormattedValue({
        value,
        format,
        useGrouping,
        locales,
        decimalSign,
        maximumFractionDigits,
      })

      setDisplayValue(formattedValue)
    }, [value, format, useGrouping, locales, decimalSign, maximumFractionDigits])

    return (
      <InputWrap
        classes={classes}
        margin={margin}
        label={label}
        value={value}
        wrapper={wrapper}
        disabled={disabled}
        startContent={startContent}
        endContent={endContent}
        focused={focused}
      >
        <input
          disabled={disabled}
          data-testid={'inputNumberField'}
          className={cn(styles.control, {
            [styles.focused]: label && (focused || value),
            [styles.noSpin]: !hasSpin,
            [styles.textAlignRight]: align === 'right',
          })}
          id={id}
          value={displayValue}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          placeholder={placeholder}
          min={min}
          max={max}
          onSelect={onSelect}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          onChange={handleOnChange}
          ref={internalRef}
        />
      </InputWrap>
    )
  }
)
